import {
	animate,
	domMax,
	LazyMotion,
	m,
	useAnimationFrame,
	useMotionValue,
} from "motion/react";
import * as React from "react";
import { cn } from "../../lib/utils";
import type { CircularProgressProps } from "./types";
import { easeInOutCubic, generateWavyCircularPath } from "./utils";

// --- M3 Kotlin Constants (from ProgressIndicator.kt) ---
// Global rotation: 1080° (3 full turns) over 6 seconds, linear
const GLOBAL_ROTATION_DURATION = 6000;
const GLOBAL_ROTATION_TARGET = 1080;

// Additional rotation: 90° stepped increments, 1.5s per cycle
// Each cycle: ease during first 500ms, then hold for remaining 1000ms
const ADDITIONAL_ROTATION_CYCLE = 1500;
const ADDITIONAL_ROTATION_STEP = 90;
const ADDITIONAL_ROTATION_EASE_DURATION = 500;

// Progress oscillation: arc fraction oscillates between min and max
const DEFAULT_MIN_PROGRESS = 0.1; // 36°
const DEFAULT_MAX_PROGRESS = 0.8; // 288°
const PROGRESS_CYCLE_DURATION = 1500; // 1.5s per half-cycle (grow or shrink)

export const CircularProgress = React.forwardRef<
	HTMLDivElement,
	CircularProgressProps
>(
	(
		{
			value,
			size = 48,
			trackHeight = 4,
			shape = "flat",
			trackShape = "flat",
			amplitude,
			wavelength,
			gapSize = 4,
			waveSpeed = 1,
			crawlerSpeed = 1,
			color,
			trackColor,
			showTrack = "auto",
			minProgress = DEFAULT_MIN_PROGRESS,
			maxProgress = DEFAULT_MAX_PROGRESS,
			determinateAnimation = "md3",
			amplitudeRange,
			className,
			"aria-label": ariaLabel,
			...restProps
		},
		ref,
	) => {
		const isDeterminate = value !== undefined;
		const clampedValue = isDeterminate ? Math.min(100, Math.max(0, value)) : 0;

		const radius = (size - trackHeight) / 2;
		const center = size / 2;

		const activeColor = color || "var(--md-sys-color-indicator-active)";
		const bgTrackColor = trackColor || "var(--md-sys-color-indicator-track)";

		const isWavy = shape === "wavy";

		// Resolve track visibility
		const shouldShowIndeterminateTrack =
			showTrack === "auto" ? isWavy : showTrack;
		const shouldShowDeterminateTrack = showTrack === "auto" ? true : showTrack;

		const BASELINE_SIZE = 48;
		const scaleFactor = size / BASELINE_SIZE;
		const effectiveAmplitude = React.useMemo(
			() => amplitude ?? 1.6 * scaleFactor,
			[amplitude, scaleFactor],
		);
		const effectiveWavelength = React.useMemo(
			() => wavelength ?? 15 * scaleFactor,
			[wavelength, scaleFactor],
		);

		// Resolved amplitude range for wavy indeterminate
		const resolvedAmplitudeRange = React.useMemo<[number, number]>(
			() => amplitudeRange ?? [0, effectiveAmplitude],
			[amplitudeRange, effectiveAmplitude],
		);

		// Static wavy path for initial layout/SSR
		const wavyActivePath = React.useMemo(
			() =>
				isWavy
					? generateWavyCircularPath(
							center,
							radius,
							effectiveAmplitude,
							effectiveWavelength,
						)
					: null,
			[isWavy, center, radius, effectiveAmplitude, effectiveWavelength],
		);

		const circumference = React.useMemo(() => 2 * Math.PI * radius, [radius]);
		const gapForTrack = React.useMemo(
			() => (gapSize + trackHeight) / circumference,
			[gapSize, trackHeight, circumference],
		);
		const activeAngularFraction = isDeterminate ? clampedValue / 100 : 0;

		const trackOffset = isDeterminate ? activeAngularFraction + gapForTrack : 0;
		const trackLength = isDeterminate
			? Math.max(0.001, 1 - activeAngularFraction - 2 * gapForTrack)
			: 1;

		const ActiveCircleElem = m.circle;

		const indeterminateSvgRef = React.useRef<SVGSVGElement>(null);
		const indeterminateTrackRef = React.useRef<SVGElement>(null);
		const indeterminateActiveRef = React.useRef<SVGElement>(null);
		const indeterminateWavyTrackPathRef = React.useRef<SVGPathElement>(null);
		const indeterminateWavyActivePathRef = React.useRef<SVGPathElement>(null);
		const determinateWavyActivePathRef = React.useRef<SVGPathElement>(null);
		const determinateWavyTrackPathRef = React.useRef<SVGPathElement>(null);
		const cachedPathLengthRef = React.useRef<number>(0);

		const fractionMV = useMotionValue(isDeterminate ? clampedValue / 100 : 0);
		const amplitudeMV = useMotionValue(isWavy ? effectiveAmplitude : 0);

		React.useEffect(() => {
			if (isDeterminate) {
				animate(fractionMV, clampedValue / 100, {
					duration: 0.4,
					ease: [0.2, 0, 0, 1],
				});
			}
		}, [clampedValue, isDeterminate, fractionMV]);

		React.useEffect(() => {
			if (!isDeterminate || !isWavy) return;
			const fraction = clampedValue / 100;
			let targetAmp = effectiveAmplitude;
			if (determinateAnimation === "md3") {
				targetAmp =
					fraction <= 0.1 || fraction >= 0.95 ? 0 : effectiveAmplitude;
			}
			animate(amplitudeMV, targetAmp, {
				type: "spring",
				bounce: 0,
				duration: 0.5,
			});
		}, [
			clampedValue,
			isDeterminate,
			isWavy,
			effectiveAmplitude,
			amplitudeMV,
			determinateAnimation,
		]);

		React.useLayoutEffect(() => {
			if (!isWavy || !wavyActivePath) {
				cachedPathLengthRef.current = circumference;
				return;
			}
			const el = (indeterminateActiveRef.current ||
				determinateWavyActivePathRef.current) as SVGPathElement | null;
			if (el && "getTotalLength" in el) {
				try {
					const len = el.getTotalLength();
					if (len > 0) {
						cachedPathLengthRef.current = len;
						return;
					}
				} catch {
					// Fallback below
				}
			}
			cachedPathLengthRef.current = circumference;
		}, [isWavy, circumference, wavyActivePath]);

		useAnimationFrame((time) => {
			const safeWaveSpeed = Math.max(0.1, waveSpeed);
			const wavePhase = (time / 1000) * safeWaveSpeed * 2 * Math.PI;

			if (isDeterminate) {
				if (isWavy) {
					const currentAmp = amplitudeMV.get();
					const dynamicPath = generateWavyCircularPath(
						center,
						radius,
						currentAmp,
						effectiveWavelength,
						wavePhase,
					);

					if (determinateWavyActivePathRef.current) {
						determinateWavyActivePathRef.current.setAttribute("d", dynamicPath);
					}
					if (trackShape === "wavy" && determinateWavyTrackPathRef.current) {
						determinateWavyTrackPathRef.current.setAttribute("d", dynamicPath);
					}

					const pathEl = determinateWavyActivePathRef.current;
					let totalLen = cachedPathLengthRef.current || circumference;
					if (pathEl) {
						try {
							const len = pathEl.getTotalLength();
							if (len > 0) totalLen = len;
						} catch {
							// Fallback
						}
					}

					const activeFrac = Math.max(0, Math.min(1, fractionMV.get()));
					const activeLen = totalLen * activeFrac;

					if (determinateWavyActivePathRef.current) {
						determinateWavyActivePathRef.current.setAttribute(
							"stroke-dasharray",
							`${activeLen} ${totalLen}`,
						);
						determinateWavyActivePathRef.current.setAttribute(
							"stroke-dashoffset",
							"0",
						);
					}

					if (
						shouldShowDeterminateTrack &&
						trackShape === "wavy" &&
						determinateWavyTrackPathRef.current
					) {
						const gapFrac = (gapSize + trackHeight) / totalLen;
						const trackStartFrac = activeFrac + gapFrac;
						const trackLen = Math.max(
							0.001,
							totalLen * (1 - activeFrac - 2 * gapFrac),
						);
						const trackOff = -totalLen * trackStartFrac;

						determinateWavyTrackPathRef.current.setAttribute(
							"stroke-dasharray",
							`${trackLen} ${totalLen}`,
						);
						determinateWavyTrackPathRef.current.setAttribute(
							"stroke-dashoffset",
							`${trackOff}`,
						);
					}
				}
				return;
			}

			const safeCrawlerSpeed = Math.max(0.1, crawlerSpeed);
			const scaledTime = time * safeCrawlerSpeed;

			// --- 1. ROTATION (M3 Kotlin dual-rotation system) ---

			// Global rotation: linear 1080° over 6s
			const globalCycle = scaledTime % GLOBAL_ROTATION_DURATION;
			const globalAngle =
				(globalCycle / GLOBAL_ROTATION_DURATION) * GLOBAL_ROTATION_TARGET;

			// Additional rotation: stepped 90° per 1.5s cycle
			// Each cycle: eases in first 500ms, then holds for 1000ms
			const additionalFullCycles = Math.floor(
				scaledTime / ADDITIONAL_ROTATION_CYCLE,
			);
			const additionalCycleTime = scaledTime % ADDITIONAL_ROTATION_CYCLE;
			const additionalEaseT = Math.min(
				1,
				additionalCycleTime / ADDITIONAL_ROTATION_EASE_DURATION,
			);
			const additionalAngle =
				(additionalFullCycles + easeInOutCubic(additionalEaseT)) *
				ADDITIONAL_ROTATION_STEP;

			const totalRotation = globalAngle + additionalAngle;

			if (indeterminateSvgRef.current) {
				indeterminateSvgRef.current.style.transform = `rotate(${totalRotation - 90}deg)`;
			}

			// --- 2. ARC PROGRESS (M3 Kotlin oscillation) ---

			// Full cycle = grow (1.5s) + shrink (1.5s) = 3s
			const progressFullCycle = PROGRESS_CYCLE_DURATION * 2;
			const progressCycleTime = scaledTime % progressFullCycle;

			let progress: number;
			if (progressCycleTime < PROGRESS_CYCLE_DURATION) {
				// Growing phase: MIN → MAX
				const t = progressCycleTime / PROGRESS_CYCLE_DURATION;
				progress =
					minProgress + (maxProgress - minProgress) * easeInOutCubic(t);
			} else {
				// Shrinking phase: MAX → MIN
				const t =
					(progressCycleTime - PROGRESS_CYCLE_DURATION) /
					PROGRESS_CYCLE_DURATION;
				progress =
					maxProgress - (maxProgress - minProgress) * easeInOutCubic(t);
			}

			// --- 3. WAVY AMPLITUDE MODULATION ---

			if (isWavy) {
				// Amplitude scales with progress:
				// short arc (min) → amplitude approaches amplitudeRange[0] (flat)
				// long arc (max) → amplitude approaches amplitudeRange[1] (full wave)
				const amplitudeT =
					(progress - minProgress) / (maxProgress - minProgress);
				const currentAmplitude =
					resolvedAmplitudeRange[0] +
					(resolvedAmplitudeRange[1] - resolvedAmplitudeRange[0]) * amplitudeT;

				// Regenerate wavy path with current amplitude and wavePhase
				const dynamicPath = generateWavyCircularPath(
					center,
					radius,
					currentAmplitude,
					effectiveWavelength,
					wavePhase,
				);

				// Update both track and active indicator paths
				if (indeterminateWavyActivePathRef.current) {
					indeterminateWavyActivePathRef.current.setAttribute("d", dynamicPath);
				}
				if (indeterminateWavyTrackPathRef.current) {
					indeterminateWavyTrackPathRef.current.setAttribute("d", dynamicPath);
				}

				// Get path length for dasharray calculation
				const pathEl = indeterminateWavyActivePathRef.current;
				let totalLen = cachedPathLengthRef.current || circumference;
				if (pathEl) {
					try {
						const len = pathEl.getTotalLength();
						if (len > 0) totalLen = len;
					} catch {
						// use cached
					}
				}

				// --- 4. STROKE-DASHARRAY for wavy (with gap) ---
				const gapFrac = (gapSize + trackHeight) / totalLen;
				const activeLen = totalLen * progress;
				const activeOff = 0; // Arc starts at 0 (rotation handles positioning)

				if (indeterminateWavyActivePathRef.current) {
					indeterminateWavyActivePathRef.current.setAttribute(
						"stroke-dasharray",
						`${activeLen} ${totalLen}`,
					);
					indeterminateWavyActivePathRef.current.setAttribute(
						"stroke-dashoffset",
						`${activeOff}`,
					);
				}

				if (
					shouldShowIndeterminateTrack &&
					indeterminateWavyTrackPathRef.current
				) {
					const trackStartFrac = progress + gapFrac;
					const trackLen = Math.max(
						0.001,
						totalLen * (1 - progress - 2 * gapFrac),
					);
					const trackOff = -totalLen * trackStartFrac;

					indeterminateWavyTrackPathRef.current.setAttribute(
						"stroke-dasharray",
						`${trackLen} ${totalLen}`,
					);
					indeterminateWavyTrackPathRef.current.setAttribute(
						"stroke-dashoffset",
						`${trackOff}`,
					);
				}
			} else {
				// --- 5. FLAT CIRCLE (stroke-dasharray on circle element) ---
				const totalLen = circumference;
				const activeLen = totalLen * progress;
				const activeOff = 0;

				if (indeterminateActiveRef.current) {
					indeterminateActiveRef.current.setAttribute(
						"stroke-dasharray",
						`${activeLen} ${totalLen}`,
					);
					indeterminateActiveRef.current.setAttribute(
						"stroke-dashoffset",
						`${activeOff}`,
					);
				}

				if (shouldShowIndeterminateTrack && indeterminateTrackRef.current) {
					const gapFrac = (gapSize + trackHeight) / totalLen;
					const trackStartFrac = progress + gapFrac;
					const trackLen = Math.max(
						0.001,
						totalLen * (1 - progress - 2 * gapFrac),
					);
					const trackOff = -totalLen * trackStartFrac;

					indeterminateTrackRef.current.setAttribute(
						"stroke-dasharray",
						`${trackLen} ${totalLen}`,
					);
					indeterminateTrackRef.current.setAttribute(
						"stroke-dashoffset",
						`${trackOff}`,
					);
				}
			}
		});

		return (
			<div
				ref={ref}
				role="progressbar"
				aria-label={ariaLabel}
				aria-valuenow={isDeterminate ? clampedValue : undefined}
				aria-valuemin={0}
				aria-valuemax={100}
				className={cn(
					"relative inline-flex items-center justify-center shrink-0",
					className,
				)}
				style={{ width: size, height: size }}
				{...restProps}
			>
				<LazyMotion features={domMax} strict>
					<svg
						width={size}
						height={size}
						viewBox={`0 0 ${size} ${size}`}
						aria-hidden="true"
						style={{ transform: "rotate(-90deg)", overflow: "visible" }}
					>
						{isDeterminate ? (
							isWavy ? (
								<>
									{shouldShowDeterminateTrack &&
										(trackShape === "wavy" ? (
											<path
												ref={determinateWavyTrackPathRef}
												d={wavyActivePath ?? ""}
												fill="none"
												stroke={bgTrackColor}
												strokeWidth={trackHeight}
												strokeLinecap="round"
											/>
										) : (
											<m.circle
												cx={center}
												cy={center}
												r={radius}
												fill="none"
												stroke={bgTrackColor}
												strokeWidth={trackHeight}
												initial={{
													pathLength: trackLength,
													pathOffset: trackOffset,
												}}
												animate={{
													pathLength: trackLength,
													pathOffset: trackOffset,
												}}
												transition={{ duration: 0.4, ease: [0.2, 0, 0, 1] }}
												strokeLinecap="round"
											/>
										))}
									<path
										ref={determinateWavyActivePathRef}
										d={wavyActivePath ?? ""}
										fill="none"
										stroke={activeColor}
										strokeWidth={trackHeight}
										strokeLinecap="round"
									/>
								</>
							) : (
								<>
									<m.circle
										cx={center}
										cy={center}
										r={radius}
										fill="none"
										stroke={bgTrackColor}
										strokeWidth={trackHeight}
										initial={{
											pathLength: trackLength,
											pathOffset: trackOffset,
										}}
										animate={{
											pathLength: trackLength,
											pathOffset: trackOffset,
										}}
										transition={{ duration: 0.4, ease: [0.2, 0, 0, 1] }}
										strokeLinecap="round"
									/>
									<ActiveCircleElem
										cx={center}
										cy={center}
										r={radius}
										fill="none"
										stroke={activeColor}
										strokeWidth={trackHeight}
										strokeLinecap="round"
										initial={{ pathLength: 0 }}
										animate={{ pathLength: clampedValue / 100 }}
										transition={{
											duration: 0.4,
											ease: [0.2, 0, 0, 1],
										}}
									/>
								</>
							)
						) : null}
					</svg>

					{!isDeterminate && (
						<svg
							ref={indeterminateSvgRef}
							width={size}
							height={size}
							viewBox={`0 0 ${size} ${size}`}
							aria-hidden="true"
							style={{
								position: "absolute",
								inset: 0,
								overflow: "visible",
								transformOrigin: "center",
								transform: "rotate(-90deg)",
							}}
						>
							{isWavy ? (
								<>
									{shouldShowIndeterminateTrack && (
										<path
											ref={indeterminateWavyTrackPathRef}
											d={wavyActivePath ?? ""}
											fill="none"
											stroke={bgTrackColor}
											strokeWidth={trackHeight}
											strokeLinecap="round"
										/>
									)}
									<path
										ref={indeterminateWavyActivePathRef}
										d={wavyActivePath ?? ""}
										fill="none"
										stroke={activeColor}
										strokeWidth={trackHeight}
										strokeLinecap="round"
									/>
								</>
							) : (
								<>
									{shouldShowIndeterminateTrack && (
										<circle
											ref={
												indeterminateTrackRef as React.RefObject<SVGCircleElement>
											}
											cx={center}
											cy={center}
											r={radius}
											fill="none"
											stroke={bgTrackColor}
											strokeWidth={trackHeight}
											strokeLinecap="round"
										/>
									)}
									<circle
										ref={
											indeterminateActiveRef as React.RefObject<SVGCircleElement>
										}
										cx={center}
										cy={center}
										r={radius}
										fill="none"
										stroke={activeColor}
										strokeWidth={trackHeight}
										strokeLinecap="round"
									/>
								</>
							)}
						</svg>
					)}
				</LazyMotion>
			</div>
		);
	},
);

CircularProgress.displayName = "CircularProgress";
